import { alert } from '@/services/alert';
import { Menu, Stack, Text } from '@mantine/core';
import { IconCopy, IconDotsVertical } from '@tabler/icons-react';
import { ActionIcon } from '@mantine/core';
import { Fragment, ReactElement, ReactNode } from 'react';

/** The teal every timeline card menu already uses for its item glyphs. */
const MENU_ICON_COLOR = '#246A7A';

export interface CardMenuDetail {
  /** The fact's name — "From", "Patient number", "Confirmation number". */
  label: string;
  /** The fact. Rendered verbatim and copied verbatim. */
  value: string;
  /** Overrides the copied string when it differs from what reads best —
   * e.g. a phone shown as (617) 555-0142 but copied as +16175550142. */
  copyValue?: string;
}

export interface CardMenuProps {
  /**
   * The verbs — anything that changes state or navigates. Pass `Menu.Item`s.
   *
   * Only what did NOT earn a place in the header cluster: an action surfaced
   * as its own icon (Reply, Mark complete) does not repeat here. One action,
   * one affordance — a duplicated item is a second copy of its own
   * enabled/disabled logic, and those drift.
   */
  children?: ReactNode;
  /**
   * The nouns — facts that identify this record, each copyable.
   *
   * The bar for earning a slot is "would someone retype this somewhere else":
   * a number they dial, an address they look up, a confirmation they quote
   * into a ticket. NOT things already on the card (timestamp, status, patient
   * name) — those are displayed, not transcribed, and padding this list is
   * what stops people scanning it.
   */
  details?: CardMenuDetail[];
  /** Menu width; details rows are the usual reason to widen it. */
  width?: number;
  'aria-label'?: string;
}

/**
 * The timeline card overflow menu: **actions above, details below**.
 *
 * Two sections, two parts of speech. Verbs change something; nouns identify
 * the record. `Copy link` belongs with the verbs even though it copies —
 * it is about the card's address in Orbit, not a fact about the message.
 *
 * Details rows carry the copy affordance on the row itself (icon, hover,
 * toast) rather than in the section header, which is why the header can stay
 * a plain content label. Naming the interaction there — "Click to copy" —
 * reads as a disabled menu item and tells you nothing about what is below it.
 *
 * Both sections are labelled. A lone "Details" header reads as a caption on
 * an afterthought; the pair reads as the menu's structure, which is the point
 * when many card types share it.
 */
export function CardMenu({ children, details, width = 240, ...rest }: CardMenuProps): ReactElement {
  const copyableDetails = (details ?? []).filter((detail) => detail.value.trim().length > 0);

  const copy = async (detail: CardMenuDetail): Promise<void> => {
    const value = detail.copyValue ?? detail.value;
    try {
      await navigator.clipboard.writeText(value);
      alert.success(`Copied ${value}`);
    } catch {
      alert.error(`Could not copy the ${detail.label.toLowerCase()}`);
    }
  };

  return (
    <Menu shadow="md" width={width} position="bottom-end">
      <Menu.Target>
        <ActionIcon variant="subtle" color="gray" size="sm" aria-label={rest['aria-label'] ?? 'More actions'}>
          <IconDotsVertical size={18} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {children && (
          <>
            <Menu.Label>Actions</Menu.Label>
            {children}
          </>
        )}
        {copyableDetails.length > 0 && (
          <>
            <Menu.Divider />
            <Menu.Label>Details</Menu.Label>
            {copyableDetails.map((detail) => (
              <Fragment key={`${detail.label}-${detail.value}`}>
                <Menu.Item
                  leftSection={<IconCopy size={16} color={MENU_ICON_COLOR} />}
                  onClick={() => copy(detail).catch(() => undefined)}
                >
                  <Stack gap={0}>
                    <Text size="xs" c="dimmed">
                      {detail.label}
                    </Text>
                    <Text size="sm" truncate>
                      {detail.value}
                    </Text>
                  </Stack>
                </Menu.Item>
              </Fragment>
            ))}
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}

/** The teal used by every action glyph in these menus, for call sites building
 * their own `Menu.Item`s so the two sections stay visually one menu. */
export const CARD_MENU_ICON_COLOR = MENU_ICON_COLOR;
